import { ErrorHandler, Inject, Injectable, Injector, Optional } from '@angular/core';
import { Router } from '@angular/router';
import { filter, map, Observable, raceWith, switchMap, take, timer } from 'rxjs';

import { WINDOW } from '@libs/shared/utilities/tokens/window.token';
import { featureFlags } from '@libs/utility-feature-flags/constants/feature-flags.constant';
import { FeatureFlagFacadeService } from '@libs/utility-feature-flags/services/feature-flag-facade/feature-flag-facade.service';

import { PAYMENT_COMPLETE_PATH } from '../../constants/payment-complete.constant';
import { PAYMENT_APP_ORIGIN } from '../../tokens/payment-app-origin.token';
import { IntegratedPaymentOutcome } from '../../types/integrated-payment-outcome.type';

export const SESSION_STORAGE_KEY = 'isEmbeddedInIframe-paymentId';

/**
 * How long to wait for LaunchDarkly before assuming the store is not on the
 * rollout. Without a bound, an unreachable LaunchDarkly would strand the shopper
 * on the payment page after paying instead of returning them to the merchant.
 */
export const FLAG_RESOLUTION_TIMEOUT_MS = 2000;

@Injectable({
  providedIn: 'root',
})
export class IntegratedPaymentsHelperService {
  constructor(
    @Inject(WINDOW) private window: Window,
    @Inject(ErrorHandler) private errorHandler: ErrorHandler,
    // Optional: only the integrated-payments app provides it. When absent (e.g.
    // the kodypayments app, which shares this service via the brand components),
    // the internal-completion path is simply never taken.
    @Optional() @Inject(PAYMENT_APP_ORIGIN) private paymentAppOrigin: string | null,
    private router: Router,
    // Resolved lazily in #isCompletionPageEnabled rather than injected here: this
    // service is also compiled into the kodypayments and pay-by-bank bundles (via
    // the shared brand components) and constructed on every page there. Taking
    // FeatureFlagFacadeService as a constructor dependency would force those apps
    // to satisfy its ngrx Store dependency just to render a logo.
    private injector: Injector,
  ) {}

  getPaymentId() {
    return this.window.location.pathname.split('/')[1];
  }

  isIframe() {
    return this.window !== this.window.parent;
  }

  isEmbeddedInIframeFn() {
    try {
      const urlParams = new URLSearchParams(this.window.location.search);
      const isEmbeddedInIframe = !!urlParams.get('isEmbeddedInIframe');

      if (isEmbeddedInIframe && this.isIframe()) {
        return true;
      }

      // Save the paymentId to handle redirect checks
      if (this.getPaymentId() === this.window.sessionStorage.getItem(SESSION_STORAGE_KEY)) {
        return true;
      }

      return false;
    } catch (e) {
      return false;
    }
  }

  redirectToReturnUrl(returnUrl: string | undefined, outcome: IntegratedPaymentOutcome, storeId?: string): void {
    try {
      if (this.isEmbeddedInIframeFn()) {
        this.window.parent.postMessage({ type: 'PAYMENT_COMPLETE', outcome }, '*');
        return;
      }

      // kp-core requires a return_url on every payment, so its absence is a data
      // problem worth surfacing rather than silently continuing.
      if (!returnUrl) {
        throw new Error('Return url not defined');
      }

      // Read the paymentId now, not when the flag resolves below. This is usually
      // called from a guard that then cancels the navigation, and the router
      // restores the previous URL — so by the time an async callback runs, the
      // path no longer holds the paymentId and we would forward an empty one.
      const paymentId = this.getPaymentId();

      // OPI's return_url is our own completion page: there is no merchant site to
      // return to, so that page is the destination regardless of the rollout.
      // Redirecting out would just reload this same page without its paymentId.
      if (this.isInternalCompleteReturnUrl(returnUrl)) {
        this.#goToCompletionPage(paymentId);
        return;
      }

      // A merchant's shopper only sees our completion page once their store is on
      // the rollout; otherwise they go straight back, exactly as before.
      this.#isCompletionPageEnabled(storeId).subscribe((enabled) => {
        if (enabled) {
          this.#goToCompletionPage(paymentId);
          return;
        }
        this.window.location.href = returnUrl;
      });
    } catch (error) {
      this.errorHandler.handleError(error);
    }
  }

  /**
   * Only the paymentId is passed on: the completion page queries the
   * authoritative status itself, so the outcome computed here is not forwarded.
   */
  #goToCompletionPage(paymentId: string): void {
    this.router.navigate([PAYMENT_COMPLETE_PATH], {
      queryParams: { paymentId },
    });
  }

  /**
   * The completion page is rolled out per store: the flag holds a list of store
   * ids. Falls back to "not on the rollout" if LaunchDarkly does not answer in
   * time, so a flag problem can never leave the shopper stuck here.
   */
  #isCompletionPageEnabled(storeId: string | undefined): Observable<boolean> {
    const featureFlagFacadeService = this.injector.get(FeatureFlagFacadeService);
    return featureFlagFacadeService.initialized().pipe(
      filter(Boolean),
      switchMap(() => featureFlagFacadeService.isActiveForStore(featureFlags.integratedPaymentsCompletionPage, storeId)),
      raceWith(timer(FLAG_RESOLUTION_TIMEOUT_MS).pipe(map(() => false))),
      take(1),
    );
  }

  /**
   * Whether the return_url points at the completion page we host ourselves, which
   * means there is no merchant site to forward the shopper on to (the OPI case).
   */
  isInternalCompleteReturnUrl(returnUrl: string | undefined): boolean {
    // No canonical origin configured (e.g. kodypayments) => never treat as internal.
    if (!returnUrl || !this.paymentAppOrigin) {
      return false;
    }
    try {
      // Compare against our canonical origin (p-dev / p-staging / p.kody.com) rather
      // than window.location.origin so this also matches while serving from localhost
      // against a staging backend. Requiring the origin to match means we never
      // swallow a genuine external redirect that happens to share the path.
      const url = new URL(returnUrl, this.paymentAppOrigin);
      return url.origin === this.paymentAppOrigin && url.pathname.replace(/\/+$/, '') === PAYMENT_COMPLETE_PATH;
    } catch {
      return false;
    }
  }
}
